import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client from "@/api/client";
import {
  Loader2,
  Footprints,
  RefreshCw,
  Search,
  MapPin,
  Smartphone,
  Monitor,
  Tablet,
  Clock,
  MousePointerClick,
  ChevronLeft,
  ChevronRight,
  Route,
  Globe2,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const PAGE_SIZE = 25;
const RANGES = ["24h", "7d", "30d", "90d"];

function DeviceIcon({ device, className = "h-3.5 w-3.5" }) {
  if (device === "mobile") return <Smartphone className={className} />;
  if (device === "tablet") return <Tablet className={className} />;
  return <Monitor className={className} />;
}

function fmtDuration(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function rel(iso, locale) {
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale }); } catch { return ""; }
}

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="rounded-2xl border border-border p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-medium uppercase tracking-widest truncate">{label}</span>
      </div>
      <p className="font-display text-2xl font-semibold mt-2 tabular-nums" dir="ltr">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </Card>
  );
}

function MiniList({ title, rows, icon: Icon }) {
  const total = (rows || []).reduce((s, r) => s + r.count, 0) || 1;
  return (
    <Card className="rounded-2xl border border-border p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{title}</p>
      </div>
      {(rows || []).length === 0 ? (
        <p className="text-xs text-muted-foreground py-3 text-center">—</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 6).map((r) => (
            <li key={r.label} className="space-y-1">
              <div className="flex items-center justify-between text-xs gap-2">
                <span className="truncate">{r.label || "(unknown)"}</span>
                <span className="font-mono text-muted-foreground tabular-nums">{r.count}</span>
              </div>
              <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${Math.round((r.count / total) * 100)}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * AdminVisitorSessions — browsable anonymous visitor journeys: where each
 * session landed, how far it went, from which country/device, and whether it
 * converted. Clicking a row reveals the full page-by-page trail.
 */
export function AdminVisitorSessions() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const dfLocale = isAr ? arLocale : undefined;
  // Backend stores raw lowercase enums (desktop / direct / organic …) — map
  // them so the Arabic console never shows stray English tokens.
  const label = (v) => (v ? t(`visitorLabels.${String(v).toLowerCase()}`, { defaultValue: v }) : "—");
  const [range, setRange] = useState("7d");
  const [converted, setConverted] = useState("all");
  const [device, setDevice] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const params = useMemo(() => {
    const p = { rng: range, converted, limit: PAGE_SIZE, skip };
    if (device) p.device = device;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [range, converted, device, q, skip]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        client.get("/admin/visitors/summary", { params: { rng: range } }),
        client.get("/admin/visitors/sessions", { params }),
      ]);
      setSummary(s.data);
      setItems(list.data.items || []);
      setTotal(list.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminVisitors.loadError", { defaultValue: "Failed to load visitor sessions" }));
    } finally {
      setLoading(false);
    }
  }, [params, range, t]);

  useEffect(() => {
    const id = setTimeout(load, 200);
    return () => clearTimeout(id);
  }, [load]);

  const openDetail = async (sessionId) => {
    setDetailLoading(true);
    setDetail({ session: { session_id: sessionId }, events: [] });
    try {
      const res = await client.get(`/admin/visitors/sessions/${sessionId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(t("adminVisitors.detailError", { defaultValue: "Could not load that journey" }));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="admin-visitor-sessions-panel">
      <Card className="rounded-2xl border border-border p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Footprints className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-semibold truncate">
                {t("adminVisitors.title", { defaultValue: "Visitor journeys" })}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {t("adminVisitors.subtitle", { defaultValue: "Anonymous session-by-session view of what visitors did before they signed up — or left." })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={range} onValueChange={(v) => { setRange(v); setSkip(0); }}>
              <TabsList className="rounded-xl h-9">
                {RANGES.map((r) => (
                  <TabsTrigger key={r} value={r} className="rounded-lg text-xs" data-testid={`admin-visitors-range-${r}`}>{r}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={load} disabled={loading} data-testid="admin-visitors-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Stat icon={Footprints} label={t("adminVisitors.sessions", { defaultValue: "Sessions" })} value={summary?.sessions ?? "—"} sub={`${summary?.unique_visitors ?? 0} ${t("adminVisitors.visitors", { defaultValue: "visitors" })}`} testId="admin-visitors-stat-sessions" />
        <Stat icon={MousePointerClick} label={t("adminVisitors.converted", { defaultValue: "Converted" })} value={summary?.converted_sessions ?? "—"} sub={`${summary?.conversion_rate_pct ?? 0}% ${t("adminVisitors.ofSessions", { defaultValue: "of sessions" })}`} testId="admin-visitors-stat-converted" />
        <Stat icon={Clock} label={t("adminVisitors.avgDuration", { defaultValue: "Avg. time on site" })} value={summary ? fmtDuration(summary.avg_duration_seconds) : "—"} sub={`${summary?.avg_pageviews ?? 0} ${t("adminVisitors.pagesPerSession", { defaultValue: "pages / session" })}`} testId="admin-visitors-stat-duration" />
        <Stat icon={Route} label={t("adminVisitors.engaged", { defaultValue: "Engaged (3+ pages)" })} value={summary?.engaged_sessions ?? "—"} testId="admin-visitors-stat-engaged" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
        <MiniList title={t("adminVisitors.topEntry", { defaultValue: "Landing pages" })} rows={summary?.top_entry_pages} icon={Route} />
        <MiniList title={t("adminVisitors.topCountries", { defaultValue: "Countries" })} rows={summary?.top_countries} icon={Globe2} />
        <MiniList title={t("adminVisitors.topSources", { defaultValue: "Sources" })} rows={(summary?.top_sources || []).map((r) => ({ ...r, label: label(r.label) }))} icon={MousePointerClick} />
        <MiniList title={t("adminVisitors.topDevices", { defaultValue: "Devices" })} rows={(summary?.top_devices || []).map((r) => ({ ...r, label: label(r.label) }))} icon={Smartphone} />
      </div>

      <Card className="rounded-2xl border border-border p-3 sm:p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px] gap-2.5">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              placeholder={t("adminVisitors.searchPlaceholder", { defaultValue: "Filter by page, country or browser" })}
              className="rounded-xl ps-9 h-10"
              data-testid="admin-visitors-search"
            />
          </div>
          <Select value={converted} onValueChange={(v) => { setConverted(v); setSkip(0); }}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-visitors-converted-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("adminVisitors.filterAll", { defaultValue: "All sessions" })}</SelectItem>
              <SelectItem value="yes">{t("adminVisitors.filterConverted", { defaultValue: "Converted only" })}</SelectItem>
              <SelectItem value="no">{t("adminVisitors.filterDropped", { defaultValue: "Did not convert" })}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={device || "_all"} onValueChange={(v) => { setDevice(v === "_all" ? "" : v); setSkip(0); }}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-visitors-device-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t("adminVisitors.allDevices", { defaultValue: "All devices" })}</SelectItem>
              <SelectItem value="desktop">Desktop</SelectItem>
              <SelectItem value="mobile">Mobile</SelectItem>
              <SelectItem value="tablet">Tablet</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-visitors-empty">
          <Footprints className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {t("adminVisitors.empty", { defaultValue: "No visitor sessions in this window yet." })}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-visitors-list">
          {items.map((s) => (
            <button
              key={s.session_id}
              type="button"
              onClick={() => openDetail(s.session_id)}
              className="w-full text-start rounded-2xl border border-border bg-card p-3.5 sm:p-4 transition-all hover:border-primary/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid={`admin-visitor-session-${s.session_id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={`text-[10px] uppercase tracking-widest ${s.conversions_count > 0 ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/25" : "bg-muted text-muted-foreground border-border"}`}
                    >
                      {s.conversions_count > 0
                        ? t("adminVisitors.badgeConverted", { defaultValue: "converted" })
                        : t("adminVisitors.badgeBrowsed", { defaultValue: "browsed" })}
                    </Badge>
                    <span className="text-sm font-medium truncate">{s.entry_path || "/"}</span>
                    {s.exit_path && s.exit_path !== s.entry_path && (
                      <span className="text-xs text-muted-foreground truncate">→ {s.exit_path}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap text-[11px] text-muted-foreground mt-1.5">
                    <span className="inline-flex items-center gap-1"><Route className="h-3 w-3" />{s.page_views_count} {t("adminVisitors.pages", { defaultValue: "pages" })}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDuration(s.duration_seconds)}</span>
                    {s.country && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.country}</span>}
                    <span className="inline-flex items-center gap-1"><DeviceIcon device={s.device_type} className="h-3 w-3" />{label(s.device_type)} · {s.browser}</span>
                    {s.source && <span>· {label(s.source)}</span>}
                  </div>
                </div>
                <div className="sm:text-end shrink-0">
                  <p className="text-[11px] font-medium">{rel(s.last_seen_at, dfLocale)}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{s.session_id.slice(0, 10)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground tabular-nums">
            {skip + 1}–{Math.min(total, skip + PAGE_SIZE)} / {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-lg h-9" onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))} disabled={skip === 0 || loading} data-testid="admin-visitors-prev">
              {isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">{Math.floor(skip / PAGE_SIZE) + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" className="rounded-lg h-9" onClick={() => setSkip((s) => s + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total || loading} data-testid="admin-visitors-next">
              {isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl rounded-2xl" data-testid="admin-visitor-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("adminVisitors.detailTitle", { defaultValue: "Session trail" })}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: t("adminVisitors.pages", { defaultValue: "pages" }), value: detail.session?.page_views_count },
                  { label: t("adminVisitors.detailDuration", { defaultValue: "duration" }), value: fmtDuration(detail.session?.duration_seconds) },
                  { label: t("adminVisitors.detailCountry", { defaultValue: "country" }), value: detail.session?.country || "—" },
                  { label: t("adminVisitors.detailDevice", { defaultValue: "device" }), value: label(detail.session?.device_type) },
                ].map((cell) => (
                  <div key={cell.label} className="rounded-xl border border-border p-2.5">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{cell.label}</p>
                    <p className="text-sm font-medium mt-0.5 truncate">{cell.value ?? "—"}</p>
                  </div>
                ))}
              </div>
              <ol className="relative border-s border-border ps-4 space-y-3 max-h-[45vh] overflow-y-auto" data-testid="admin-visitor-detail-events">
                {(detail.events || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("adminVisitors.noEvents", { defaultValue: "No events recorded for this session." })}</p>
                ) : (
                  detail.events.map((ev, i) => (
                    <li key={`${ev.created_at}-${i}`} className="relative">
                      <span className="absolute -start-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                      <p className="text-sm truncate">{ev.path || "/"}</p>
                      <p className="text-[11px] text-muted-foreground">
                        <span className="capitalize">{(ev.event_type || "").replace(/_/g, " ")}</span>
                        {" · "}
                        {(() => { try { return format(parseISO(ev.created_at), "MMM d · HH:mm:ss", { locale: dfLocale }); } catch { return ev.created_at; } })()}
                      </p>
                    </li>
                  ))
                )}
              </ol>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminVisitorSessions;
